import datapoints from '../datapoints';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getApolloClient } from 'owa-apollo';
import { MailboxInfoInput } from 'owa-graph-schema';
import { TableView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    MarkItemAsPhishingDocument,
    MarkItemAsPhishingMutation,
} from '../../graphql/__generated__/markItemAsPhishingMutation.interface';

type MarkItemAsPhishingResult = MarkItemAsPhishingMutation['markItemAsPhishing'];

/**
 * The action which marks items as phishing.
 * @param itemIds - the itemIds to mark as phishing * @param actionSource - where this mark as phishing action is triggered
 * @param markAsPhishingStoreUpdate the callback function to update the store upon markItemsAsPhishingDatapoint as phishing
 * @returns a promise to end the markItemsAsPhishingDatapoint datapoint
 */
export default createLegacyActionWithDatapoint(
    'markItemsAsPhishing',
    datapoints.markItemsAsPhishing
)(function markItemsAsPhishingBasedOnItemIds(
    itemIds: string[],
    tableView: TableView,
    actionSource: ActionSource,
    markAsPhishingStoreUpdate: () => void
): Promise<void> {
    const reportJunkEmailEnabledByAdmin = getUserConfiguration().SegmentationSettings
        .ReportJunkEmailEnabled;

    // The ReportJunkEmailEnabled parameter specifies whether users can report messages to Microsoft from messages in Outlook on the web.
    // When this parameter is set to false, the Report junk, Report phishing, Report not junk and Unsubscribe options aren't available.
    // Users can stil mark messages as junk, phishing, or not junk, but they won't be able to report messages to Microsoft.
    if (reportJunkEmailEnabledByAdmin) {
        try {
            exportedHelperFunctions.invokeMarkItemAsPhishingMutation(
                itemIds,
                getMailboxInfo(tableView)
            );
        } catch (error) {
            // VSO 19006: Handle service error caused by block
        }
    }

    markAsPhishingStoreUpdate();

    // Synchronously returns the promise to end the markItemsAsPhishingDatapoint datapoint
    return Promise.resolve();
});

const invokeMarkItemAsPhishingMutationFn = async function invokeMarkItemAsPhishingMutation(
    ids: string[],
    mailboxInfo: MailboxInfoInput
): Promise<MarkItemAsPhishingResult> {
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            ids: ids,
            mailboxInfo: mailboxInfo,
        },
        mutation: MarkItemAsPhishingDocument,
    });

    return result?.data?.markItemAsPhishing;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeMarkItemAsPhishingMutation: invokeMarkItemAsPhishingMutationFn,
};
